import { useState, useEffect, useRef } from 'react';
import { Phone, ArrowRight, Shield, Loader2, AlertCircle, Download } from 'lucide-react';
import { authService } from '../../services/firebase-auth.service';
import { dbService } from '../../services/firebase-db.service';

interface LoginScreenProps {
  onSuccess: () => void;
  onNavigateToSignUp?: () => void;
  onIncompleteProfile?: () => void; // New callback for incomplete profiles
}

export function LoginScreen({ onSuccess, onNavigateToSignUp, onIncompleteProfile }: LoginScreenProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const fullPhone = `+234${phoneNumber}`;
      console.log('Sending WhatsApp OTP to:', fullPhone);
      
      await authService.sendOTP(fullPhone);
      
      console.log('WhatsApp OTP sent successfully!');
      setStep('otp');
    } catch (err: any) {
      console.error('Failed to send OTP:', err);
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = async (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }

      // Auto-submit when complete
      if (index === 5 && value && newOtp.every(digit => digit)) {
        setLoading(true);
        setError('');
        
        try {
          const otpCode = newOtp.join('');
          const fullPhone = `+234${phoneNumber}`;
          console.log('Verifying OTP:', otpCode);
          
          const user = await authService.verifyOTP(fullPhone, otpCode);
          
          console.log('Login successful!', user);
          
          // Check if user has a complete profile
          if (user) {
            const profile = await dbService.getRiderProfile(user.uid);
            
            if (!profile) {
              console.log('No rider profile yet — continue signup');
              setLoading(false);
              if (onIncompleteProfile) {
                onIncompleteProfile();
              } else {
                setTimeout(() => onSuccess(), 300);
              }
            } else {
              console.log('✅ Profile found - proceeding to app');
              setTimeout(() => onSuccess(), 500);
            }
          } else {
            setTimeout(() => onSuccess(), 500);
          }
        } catch (err: any) {
          console.error('OTP verification failed:', err);
          setError('Invalid code. Please try again.');
          setOtp(['', '', '', '', '', '']);
          setLoading(false);
        }
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Keep the login screen within the visible viewport so the on-screen keyboard
  // doesn't cover the inputs on mobile. Falls back to full height if unsupported.
  useEffect(() => {
    const el = containerRef.current;
    const vv = window.visualViewport;
    if (!el) return;
    const apply = () => {
      const h = vv ? vv.height : window.innerHeight;
      el.style.height = `${h}px`;
    };
    apply();
    if (vv) {
      vv.addEventListener('resize', apply);
      vv.addEventListener('scroll', apply);
      return () => {
        vv.removeEventListener('resize', apply);
        vv.removeEventListener('scroll', apply);
      };
    }
    return undefined;
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 bg-white flex flex-col overflow-y-auto">
      {/* Header */}
      <div className="p-8 pt-16">
        <div className="w-16 h-16 rounded-full bg-[#E0001B]/10 flex items-center justify-center mb-6">
          <Phone className="w-8 h-8 text-[#E0001B]" />
        </div>
        
        <h1 className="text-[28px] text-[#111111] mb-2">
          {step === 'phone' ? 'Welcome back' : 'Enter verification code'}
        </h1>
        
        <p className="text-[#6B6B6B]">
          {step === 'phone' 
            ? 'Enter your mobile number to continue' 
            : `We sent a code to ${phoneNumber}`}
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 px-8">
        {step === 'phone' ? (
          <form onSubmit={handlePhoneSubmit} className="space-y-6">
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 rounded-[12px] border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-[14px] text-red-900">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="phone" className="block text-[14px] mb-2">
                Mobile Number
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-[#6B6B6B]">
                  <Phone className="w-4 h-4" />
                  <span>+234</span>
                </div>
                <input
                  id="phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                  onFocus={(e) => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' })}
                  placeholder="810 123 4567"
                  className="w-full h-14 pl-24 pr-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                  maxLength={10}
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={phoneNumber.length < 10 || loading}
              className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending WhatsApp OTP...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <div className="text-center">
              <p className="text-[14px] text-[#6B6B6B]">
                Don&apos;t have an account?{' '}
                <button 
                  type="button"
                  onClick={onNavigateToSignUp}
                  className="text-[#E0001B] hover:underline"
                >
                  Sign up
                </button>
              </p>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 rounded-[12px] border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-[14px] text-red-900">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-[14px] mb-3">
                Verification Code
              </label>
              <div className="flex gap-2 justify-between">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onFocus={(e) => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' })}
                    disabled={loading}
                    className="w-12 h-14 text-center rounded-[12px] bg-gray-50 border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors disabled:opacity-50"
                    maxLength={1}
                  />
                ))}
              </div>
              {loading && (
                <div className="flex items-center justify-center gap-2 mt-4 text-[#6B6B6B]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-[14px]">Verifying...</span>
                </div>
              )}
            </div>

            <button
              onClick={() => { setStep('phone'); setOtp(['', '', '', '', '', '']); setError(''); }}
              disabled={loading}
              className="w-full text-[14px] text-[#E0001B] hover:underline disabled:opacity-50"
            >
              Resend code
            </button>

            <div className="pt-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-[12px] border border-blue-100">
                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[14px] text-blue-900">Secure Login</p>
                  <p className="text-[12px] text-blue-700 mt-0.5">
                    We&apos;ll never share your number or send you spam
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <a
            href="https://go7.ng/api/download-rider.php"
            download="go7-rider.apk"
            className="w-full h-11 rounded-[12px] border border-[#E0001B] text-[#E0001B] hover:bg-red-50 transition-colors flex items-center justify-center gap-2 text-[14px] font-medium"
          >
            <Download className="w-4 h-4" />
            Download Android App
          </a>
          <p className="mt-2 text-[11px] leading-4 text-gray-500 text-center">
            To install: open the downloaded file, allow &quot;Install unknown apps&quot;
            for your browser when prompted, then tap Install.
          </p>
        </div>
      </div>
    </div>
  );
}